// GAVION A12 (B5-T12/T24 carry): email sends that wait on an admin.
// * held   — an active sequence enrolment whose runner wrote last_error (Graph not wired, draft/archived
//            template, SES missing, a retryable failure): it retries on its own once the cause is fixed.
// * review — the send outcome is unknown and is never retried (no double send): an automation action in
//            `needs_review`, or a campaign recipient failed `needs_review:<detail>`. Shown for 30 days.
// Admin RLS reads every source (sequence_enrollments, scheduled_actions, campaign_recipients).

export type AttentionItem = {
  key: string;
  kind: "held" | "review";
  source: "sequence" | "automation" | "campaign";
  /** Runner / tick code, without the `needs_review:` prefix. */
  reason: string;
  /** Recipient address, or the automation action type. */
  target: string;
  /** Sequence or campaign name, when known. */
  label?: string;
  at?: string | null;
  link?: string;
};

type Row = Record<string, any>;

export const REVIEW_WINDOW_DAYS = 30;

export const reasonCode = (raw: unknown): string => {
  if (typeof raw !== "string" || raw.trim() === "") return "unknown";
  return raw.startsWith("needs_review:")
    ? raw.slice("needs_review:".length) || "unknown"
    : raw;
};

const ENTITY_ROUTES: Record<string, string> = {
  company: "companies",
  contact: "contacts",
  deal: "deals",
};

const nameById = (rows: Row[] | undefined) =>
  new Map((rows ?? []).map((r) => [r.id, r.name as string | undefined]));

export const collectAttention = ({
  now,
  enrollments,
  actions,
  recipients,
  sequences,
  campaigns,
}: {
  now: Date;
  enrollments?: Row[];
  actions?: Row[];
  recipients?: Row[];
  sequences?: Row[];
  campaigns?: Row[];
}): AttentionItem[] => {
  const since = now.getTime() - REVIEW_WINDOW_DAYS * 86_400_000;
  // A row without any timestamp stays visible (never hidden by mistake).
  const recent = (at: unknown) =>
    typeof at !== "string" || Number.isNaN(Date.parse(at))
      ? true
      : Date.parse(at) >= since;
  const sequenceName = nameById(sequences);
  const campaignName = nameById(campaigns);

  const held: AttentionItem[] = (enrollments ?? [])
    .filter((e) => e.status === "active" && e.last_error)
    .map((e) => ({
      key: `enrollment-${e.id}`,
      kind: "held",
      source: "sequence",
      reason: reasonCode(e.last_error),
      target: String(e.email ?? ""),
      label: sequenceName.get(e.sequence_id),
      at: e.next_run_at ?? null,
      link: `/courriels/sequences/${e.sequence_id}`,
    }));

  const automation: AttentionItem[] = (actions ?? [])
    .filter(
      (a) => a.status === "needs_review" && recent(a.claimed_at ?? a.due_at),
    )
    .map((a) => {
      const route = ENTITY_ROUTES[a.entity_type];
      return {
        key: `action-${a.id}`,
        kind: "review",
        source: "automation",
        reason: reasonCode(a.last_error),
        target: String(a.action?.type ?? ""),
        at: a.claimed_at ?? a.due_at ?? null,
        link: route ? `/${route}/${a.entity_id}/show` : undefined,
      };
    });

  const campaign: AttentionItem[] = (recipients ?? [])
    .filter(
      (r) =>
        r.status === "failed" &&
        typeof r.error === "string" &&
        r.error.startsWith("needs_review") &&
        recent(r.claimed_at ?? r.sent_at),
    )
    .map((r) => ({
      key: `recipient-${r.id}`,
      kind: "review",
      source: "campaign",
      reason: reasonCode(r.error),
      target: String(r.email ?? ""),
      label: campaignName.get(r.campaign_id),
      at: r.claimed_at ?? null,
      link: `/courriels/campagnes/${r.campaign_id}`,
    }));

  return [...held, ...automation, ...campaign];
};
